/**
 * linkSharedMailboxAccess — when a person opens an email from a shared mailbox,
 * ask the product to link them to it, so the mailbox counts as theirs (pane
 * count, Mailboxes page). The substrate proves access by possession: the
 * message must be in that mailbox and recent, checked with its own Graph access.
 * It links only an ENROLLED mailbox; the target here stays a client hint, never
 * an authorization decision.
 *
 * Best effort and quiet: once per mailbox per pane session, whatever the
 * outcome, so a refusal never costs a call per email.
 */
import { useEffect } from "react";
import { orchestratorApi } from "../../services/orchestratorApi";
import type { SharedMailboxLaunchStatus } from "./sharedMailboxTarget";

const askedThisSession = new Set<string>();

/** Test seam. */
export function _resetLinkedMailboxes(): void {
  askedThisSession.clear();
}

export function useLinkSharedMailboxAccess(
  status: SharedMailboxLaunchStatus,
  targetMailbox: string | null,
  restId: string | null,
): void {
  useEffect(() => {
    if (status !== "shared" || !targetMailbox || !restId) return;
    const key = targetMailbox.trim().toLowerCase();
    if (!key || askedThisSession.has(key)) return;
    askedThisSession.add(key);
    orchestratorApi.linkMailboxAccess(targetMailbox, restId).catch(() => {
      /* not linked this session; the pane works exactly as before */
    });
  }, [status, targetMailbox, restId]);
}
